<script setup lang="ts">
import { computed } from "vue";
import {
  andThenMaybe,
  fromMaybe,
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../../lib/maybe";
import { displayFileName } from "../../lib/fileNames";
import { trimmedMaybe } from "../../lib/trimmedString";
import { entityChipAccentStyle } from "../../lib/entityColors";
import { landmarkIconById } from "../../lib/mapLandmarkIcons";
import { useStoryboardDragStore } from "../../stores/useStoryboardDragStore";
import StaticSvgMarkup from "../ui/StaticSvgMarkup.vue";
function canRemoveChip(chip: StoryboardEntityChip): boolean {
  return (
    chip.manuallyLinked === true ||
    chip.kind === "character" ||
    chip.kind === "arc"
  );
}

async function removeChip(chip: StoryboardEntityChip): Promise<void> {
  if (isNothing(chip.entryFile)) return;
  const entryFile = chip.entryFile.value;
  try {
    if (chip.kind === "arc") {
      if (isJust(chip.momentId))
        await drag.unlinkArcMoment(entryFile, chip.momentId.value);
      return;
    }
    await drag.setSceneLink(entryFile, props.scenePath, false);
  } catch (error) {
    window.alert(
      `Unable to unlink ${chip.label}: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

function arcLabelParts(chip: StoryboardEntityChip): {
  name: string;
  moment: string;
} {
  const name = isJust(chip.entryFile)
    ? displayFileName(chip.entryFile.value)
    : "";
  const separator =
    name.length > 0 && chip.label.startsWith(`${name} · `)
      ? name.length
      : chip.label.indexOf(" · ");
  return separator < 0
    ? { name: chip.label, moment: "" }
    : {
        name: chip.label.slice(0, separator),
        moment: chip.label.slice(separator + 3),
      };
}

/** Worldbuilding links on a card — map places and arc moments linked to this
 * scene. Keyed by stable ids so a throughline view can index the same shape in
 * reverse without a data reshape. */
export type StoryboardEntityChip = {
  id: string;
  label: string;
  kind:
    | "character"
    | "location"
    | "organization"
    | "item"
    | "arc"
    | "map"
    | "entity";
  /** For `arc` chips, the `Database/` entry to open on click. */
  entryFile: Maybe<string>;
  momentId: Maybe<string>;
  manuallyLinked: boolean;
  mapLinked: boolean;
  /** Entry accent color from frontmatter. Tints arc chips. */
  color: Maybe<string>;
  /** For `map` chips, open this `.zwmap` and focus the landmark. */
  mapFile: Maybe<string>;
  landmarkId: Maybe<string>;
  /** Landmark catalog id (`city`, `castle`, …) for the chip SVG. */
  iconId: Maybe<string>;
  previewSrc: Maybe<string>;
};

const props = defineProps<{
  scenePath: string;
  entityChips: StoryboardEntityChip[];
  onChipClick: (chip: StoryboardEntityChip) => void;
  onArcChipPointerDown: Maybe<
    (chip: StoryboardEntityChip, event: PointerEvent) => void
  >;
}>();
const drag = useStoryboardDragStore();
const mapPreview = computed(
  (): Maybe<{ chip: StoryboardEntityChip; src: string }> => {
    for (const chip of props.entityChips) {
      if (chip.kind === "map" && isJust(chip.previewSrc)) {
        return just({ chip, src: chip.previewSrc.value });
      }
    }
    return nothing;
  },
);

const visibleChips = computed(() =>
  props.entityChips.filter((chip) => chip.kind !== "map"),
);

function handleChipClick(chip: StoryboardEntityChip): void {
  if (drag.consumePointerGestureWasDrag()) return;
  props.onChipClick(chip);
}

function arcChipStyle(
  chip: StoryboardEntityChip,
): Maybe<Record<string, string>> {
  return entityChipAccentStyle(chip.color);
}

function trimmedChipField(field: Maybe<string>): Maybe<string> {
  return andThenMaybe(field, (value) => trimmedMaybe(value));
}

function arcChipIsDraggable(chip: StoryboardEntityChip): boolean {
  return (
    chip.kind === "arc" &&
    isJust(props.onArcChipPointerDown) &&
    isJust(chip.momentId) &&
    chip.momentId.value.length > 0
  );
}

function arcChipIsDragging(chip: StoryboardEntityChip): boolean {
  if (
    isNothing(drag.dragFrom) ||
    isNothing(chip.entryFile) ||
    isNothing(chip.momentId)
  ) {
    return false;
  }
  return (
    drag.dragFrom.value.key ===
    `arc-moment:${chip.entryFile.value}:${chip.momentId.value}`
  );
}

function handleArcChipPointerDown(
  chip: StoryboardEntityChip,
  event: PointerEvent,
): void {
  drag.consumePointerGestureWasDrag();
  if (
    chip.kind !== "arc" ||
    isNothing(trimmedChipField(chip.entryFile)) ||
    isNothing(trimmedChipField(chip.momentId))
  ) {
    return;
  }
  if (isJust(props.onArcChipPointerDown))
    props.onArcChipPointerDown.value(chip, event);
}

/** Bundled landmark SVG markup for a map chip, or empty when unknown. */
function mapChipIconMarkup(chip: StoryboardEntityChip): string {
  if (chip.kind !== "map") return "";
  const iconId = trimmedChipField(chip.iconId);
  if (isNothing(iconId)) return "";
  return matchMaybe(landmarkIconById(iconId.value), {
    nothing: () => "",
    just: (icon) => fromMaybe(trimmedMaybe(icon.svg)) ?? "",
  });
}
</script>
<template>
  <div class="zw-board-card-metadata">
    <button
      v-if="isJust(mapPreview)"
      type="button"
      class="zw-board-map-preview"
      :aria-label="`Open map near ${mapPreview.value.chip.label}`"
      @pointerdown.stop="drag.consumePointerGestureWasDrag()"
      @click.stop="handleChipClick(mapPreview.value.chip)"
    >
      <img
        :src="mapPreview.value.src"
        :alt="`Map near ${mapPreview.value.chip.label}`"
        loading="lazy"
      />
    </button>
    <slot />
    <ul
      v-if="visibleChips.length > 0 || typeof $slots.actions === 'function'"
      class="zw-board-card-chips"
      aria-label="Linked entities"
    >
      <li
        v-for="chip in visibleChips"
        :key="chip.id"
        class="zw-board-card-chip-wrap"
        :style="fromMaybe(arcChipStyle(chip))"
        :class="{
          'zw-board-card-chip-arc-default':
            chip.kind === 'arc' && isNothing(chip.color),
        }"
      >
        <button
          type="button"
          class="zw-board-card-chip"
          :class="[
            `zw-board-card-chip-${chip.kind}`,
            arcChipIsDraggable(chip) && 'zw-board-card-chip-draggable',
            arcChipIsDragging(chip) && 'zw-board-card-chip-dragging',
          ]"
          :title="
            chip.mapLinked === true
              ? 'Linked through a map location'
              : undefined
          "
          @pointerdown.stop="handleArcChipPointerDown(chip, $event)"
          @click.stop="handleChipClick(chip)"
        >
          <StaticSvgMarkup
            v-if="mapChipIconMarkup(chip).length > 0"
            class="zw-board-card-chip-icon"
            :markup="mapChipIconMarkup(chip)"
          />
          <span
            v-if="chip.kind === 'arc' && chip.label.includes(' · ') === true"
            class="zw-board-arc-label"
            ><span>{{ arcLabelParts(chip).name }}</span> ·
            <span class="zw-board-arc-moment">{{
              arcLabelParts(chip).moment
            }}</span></span
          >
          <span v-else>{{ chip.label }}</span>
        </button>
        <button
          v-if="canRemoveChip(chip) && isJust(chip.entryFile)"
          type="button"
          class="zw-board-chip-unlink"
          :aria-label="`Unlink ${chip.label} from scene`"
          :title="
            chip.kind === 'arc'
              ? 'Unlink event from scene'
              : 'Remove character or entry from scene'
          "
          @pointerdown.stop
          @click.stop="removeChip(chip)"
        >
          ×
        </button>
      </li>
      <li
        v-if="typeof $slots.actions === 'function'"
        class="zw-board-card-chip-action"
      >
        <slot name="actions" />
      </li>
    </ul>
  </div>
</template>
<style scoped>
.zw-board-card-chip-wrap:has(.zw-board-chip-unlink:hover) .zw-board-arc-moment,
.zw-board-card-chip-wrap:has(.zw-board-chip-unlink:focus-visible)
  .zw-board-arc-moment {
  text-decoration: line-through;
}

.zw-board-chip-unlink {
  align-self: stretch;
  background: transparent;
  color: inherit;
  border: 0;
  border-radius: 0 20px 20px 0;
  padding: 0 7px 0 3px;
  /* WCAG 2.5.8: keep the unlink target at least 24px (ZW-277). */
  min-width: 24px;
  min-height: 24px;
  cursor: pointer;
  font: inherit;
  opacity: 0.75;
}
.zw-board-chip-unlink:hover {
  opacity: 1;
  background: color-mix(in oklch, currentColor 10%, transparent);
}
.zw-board-chip-unlink:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: -2px;
}

.zw-board-card-metadata {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.zw-board-card-chips {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  justify-content: flex-end;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
}
.zw-board-card-chip-wrap {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border: 1px solid var(--line);
  border-radius: 20px;
  background: var(--bg);
  color: var(--text-muted);
  font-size: 0.75rem;
}
.zw-board-map-preview {
  width: 112px;
  max-width: 100%;
  flex-shrink: 0;
  padding: 0;
  border: 1px solid var(--line);
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  background: var(--bg);
}
.zw-board-map-preview img {
  display: block;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
}
.zw-board-card-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  overflow-wrap: anywhere;
  min-width: 0;
  border: 0;
  border-radius: 20px;
  padding: 2px 8px;
  background: transparent;
  color: inherit;
  font-size: inherit;
}
button.zw-board-card-chip {
  cursor: pointer;
}
.zw-board-map-preview:focus-visible,
.zw-board-card-chip:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}
.zw-board-map-preview:hover {
  border-color: var(--text-muted);
}
.zw-board-card-chip:hover {
  background: var(--hover-fill);
}
.zw-board-card-chip-draggable {
  cursor: grab !important;
}
.zw-board-card-chip-icon {
  width: 14px;
  height: 14px;
  display: inline-flex;
}
.zw-board-card-chip-icon :deep(svg) {
  width: 100%;
  height: 100%;
}
.zw-board-card-chip-arc-default {
  background: var(--bg-raised);
  color: var(--text);
}

.zw-board-card-chip-dragging {
  opacity: 0.45;
}
@container (max-width:560px) {
  .zw-board-card-chips {
    justify-content: flex-start;
  }
}
</style>
